import { ReactElement, ReactNode } from 'react';
import useSWR from 'swr';
import { LinkerContext } from './types';
import { Alert, LoadingContainer } from '@ff-it/ui';
import { useFiancePeriod } from './useFinancePeriod';
import { ListCampaign } from 'modules/campaign/campaign/types';
import { useFieldValue } from 'hooks';
import invariant from 'tiny-invariant';

type LinksProviderProps = {
  url: string;
  children: (ctx: LinkerContext) => ReactNode;
  shouldHaveFiancePeriod: boolean;
};

export function LinksProvider({ url, children, shouldHaveFiancePeriod }: LinksProviderProps): ReactElement {
  const financePeriod = useFiancePeriod();
  const financePeriodRequired = shouldHaveFiancePeriod && !financePeriod;
  const { data, error, isLoading } = useSWR<LinkerContext>(
    financePeriodRequired
      ? null
      : {
          url: `${url}candidates/`,
          method: 'GET',
          queryParams: {
            finance_period: financePeriod ? `${financePeriod.start},${financePeriod.end}` : '',
          },
        },
    null,
    {
      keepPreviousData: false,
    },
  );

  if (error) {
    throw error;
  }

  return (
    <LoadingContainer loading={isLoading} className="flex-grow-1 flex-column d-flex">
      {financePeriodRequired ? (
        <Alert heading="Finance period required" mb="3">
          Set finance period to invoice or recalcualte
        </Alert>
      ) : data ? (
        children(data)
      ) : null}
    </LoadingContainer>
  );
}

export function BuilderLinksProvider({
  url,
  children,
}: Omit<LinksProviderProps, 'shouldHaveFiancePeriod'>): ReactElement {
  const financePeriod = useFiancePeriod();

  const source_campaigns = useFieldValue<ListCampaign[]>('source_campaigns', {
    allowNull: false,
  });
  invariant(source_campaigns);
  const { data, error, isLoading } = useSWR<LinkerContext>(
    {
      url: `${url}candidates/`,
      method: 'GET',
      queryParams: {
        finance_period: financePeriod ? `${financePeriod.start},${financePeriod.end}` : '',
        source_campaigns: source_campaigns.map((c) => c.id).join(','),
      },
    },
    null,
    { keepPreviousData: false },
  );

  if (error) {
    throw error;
  }

  return (
    <LoadingContainer loading={isLoading} className="flex-grow-1 flex-column d-flex">
      {data ? children(data) : null}
    </LoadingContainer>
  );
}
